import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { FileMetadata } from '../model/file-metadata';
import { SERVER_API_URL } from '../../../app.constants';
import { ParamContext } from '../../service/param.context';
import { Observable } from 'rxjs';
import { Pageable } from '../../model/pageable';
import { createRequestOption } from '../..';

interface FileSearchRequest {
    observationUnitUUID?: string;
    germplasmUUID?: string;
    variableName?: string;
    instanceIds?: number[];
    lotId?: number;
    projectId?: number;
}

@Injectable({ providedIn: 'root' })
export class FileService {

    constructor(
        private http: HttpClient,
        private context: ParamContext
    ) {
    }

    listFileMetadata(
        observationUnitUUID?: string,
        germplasmUUID?: string,
        variableName?: string,
        instanceId?: number,
        lotId?: number,
        projectId?: number,
        pageable?: Pageable
    ): Observable<HttpResponse<FileMetadata[]>> {
        const baseUrl = `${SERVER_API_URL}crops/${this.context.cropName}`;

        const request: FileSearchRequest = {
            ...(observationUnitUUID && { observationUnitUUID }),
            ...(germplasmUUID && { germplasmUUID }),
            ...(variableName && { variableName }),
            ...(instanceId && { instanceIds: [instanceId] }),
            ...(lotId && { lotId }),
            ...(projectId && { projectId })
        };

        const params = createRequestOption({
            programUUID: this.context.programUUID,
            ...pageable
        });

        return this.http.post<FileMetadata[]>(
            `${baseUrl}/filemetadata/search`,
            request,
            { params, observe: 'response' }
        );
    }

    upload(file: File, observationUnitUUID, germplasmUUID, termId = null, instanceId, projectId, lotId): Observable<FileMetadata> {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        const headers = new Headers();
        headers.append('Content-Type', '');

        const params = {};
        if (observationUnitUUID) {
            params['observationUnitUUID'] = observationUnitUUID;
        }
        if (germplasmUUID) {
            params['germplasmUUID'] = germplasmUUID;
        }
        if (termId) {
            params['termId'] = termId;
        }
        if (instanceId) {
            params['instanceId'] = instanceId;
        }
        if (projectId) {
            params['projectId'] = projectId;
        }
        if (lotId) {
            params['lotId'] = lotId;
        }
        const options = { params };

        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        return this.http.post<FileMetadata>(baseUrl + '/files?programUUID=' + this.context.programUUID, formData, options);
    }

    downloadFile(path): Observable<HttpResponse<Blob>> {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        return this.http.get(baseUrl + '/files/' + path, { observe: 'response', responseType: 'blob' });
    }

    delete(fileUUID) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        return this.http.delete(baseUrl + '/files/' + fileUUID + '?programUUID=' + this.context.programUUID);
    }

    getFileCount(variableIds, germplasmUUID, lotId) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = { variableIds };
        if (germplasmUUID) {
            params['germplasmUUID'] = germplasmUUID;
        }
        if (lotId) {
            params['lotId'] = lotId;
        }
        return this.http.head(baseUrl + '/filemetadata', { params, observe: 'response' });
    }

    getFileCountStudy(variableIds, datasetId, instanceId, projectId) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = { };
        if (variableIds) {
            params['variableIds'] = variableIds;
        }
        if (datasetId) {
            params['datasetId'] = datasetId;
        }
        if (instanceId) {
            params['instanceId'] = instanceId;
        }
        if (projectId) {
            params['projectId'] = projectId;
        }
        return this.http.head(baseUrl + '/filemetadata', {
            params,
            observe: 'response'
        });
    };

    detachFiles(variableIds, germplasmUUID, lotId) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = {
            variableIds,
            programUUID: this.context.programUUID
        };
        if (germplasmUUID) {
            params['germplasmUUID'] = germplasmUUID;
        }
        if (lotId) {
            params['lotId'] = lotId;
        }
        return this.http.delete(baseUrl + '/filemetadata/variables', { params });
    }

    detachFilesStudy(variableIds: any, datasetId: any, projectId: any) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = {
            variableIds,
            programUUID: this.context.programUUID
        };
        if (datasetId) {
            params['datasetId'] = datasetId;
        }
        if (projectId) {
            params['projectId'] = projectId;
        }
        return this.http.delete(baseUrl + '/filemetadata/variables', { params });
    }

    removeFiles(variableIds, germplasmUUID, lotId) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = {
            variableIds,
            programUUID: this.context.programUUID
        };
        if (germplasmUUID) {
            params['germplasmUUID'] = germplasmUUID;
        }
        if (lotId) {
            params['lotId'] = lotId;
        }
        return this.http.delete(baseUrl + '/filemetadata', { params });
    }

    removeFilesStudy(variableIds, datasetId: any, projectId) {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        const params = {
            variableIds,
            programUUID: this.context.programUUID
        };
        if (datasetId) {
            params['datasetId'] = datasetId;
        }
        if (projectId) {
            params['projectId'] = projectId;
        }
        return this.http.delete(baseUrl + '/filemetadata', { params });
    }

    isFileStorageConfigured(): Promise<boolean> {
        const baseUrl = SERVER_API_URL + 'crops/' + this.context.cropName;
        return this.http.get(baseUrl + '/filestorage/status').toPromise().then((resp: any) => resp.status);
    }

}
